import React from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";
import SheetStep from "../SheetStep";
import { Form } from "@redriver/cinnamon";
import {
  FunderType,
  UnlockFields,
} from "features/../../../shared/constants/enums";
import OverrideSection from "./OverrideSection";
import FunderRateTypeSection from "./FunderRateTypeSection";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { withImpersonatedPermissions } from "modules/ImpersonatedUserPermissions";
import { useSheetCalcInfo } from "features/Sheets";
import { CalcTableType } from "mortar/Principal/Enums";
import classNames from "classnames";

const IPSyndicateRatesStep = ({
  tieredTonerAvailableToDealer,
  sheetId,
  onNextStep,
  onPreviousStep,
  displayPrevious,
  displayNext,
  displaySave,
  collapsed,
  toggleVisibility,
  reactRef,
  calculating,
  customerToggle,
  displayCustomerToggle = false,
  hasPermission,
}) => {
  const { t } = useTranslation("ipsRates");
  const { includeStandardCalcTable, includeSoftwareCalcTable, isMssa } =
    useSheetCalcInfo();
  const calcTableTypes = [
    includeSoftwareCalcTable && CalcTableType.SoftwareAssignment,
    includeStandardCalcTable && CalcTableType.Standard,
  ].filter(Boolean);

  const UnlockFieldOptions = [
    { value: UnlockFields.Yes, text: t("Yes") },
    { value: UnlockFields.No, text: t("No") },
  ];

  if (!hasPermission(Targets.Sheet, Actions.ViewIPSyndicateRates)) {
    return null;
  }

  return (
    <div ref={reactRef} className="step-container">
      <SheetStep
        title={t("IP_Syndicate_Rates_Settings")}
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        displayPrevious={displayPrevious}
        displayNext={displayNext}
        displaySave={displaySave}
        collapsed={collapsed}
        toggleVisibility={toggleVisibility}
        calculating={calculating}
        sheetId={sheetId}
        displayCustomerToggle={displayCustomerToggle}
        customerToggle={customerToggle}
        className={"ips-rates-step"}
      >
        <Form.Object field="ipsRates">
          <div className="ips-rates-step">
            <Grid rows={3} stackable>
              <Grid.Row className="ips-rates-radio-group">
                <Grid.Column
                  mobile={8}
                  tablet={8}
                  computer={3}
                  className="override-radio-group"
                >
                  <Form.RadioGroup
                    inline
                    field="unlockDisableMinVolsLogic"
                    label={
                      <div className="override-radio-title">
                        <p>{t("Unlock_Disable_Min_Vols_Logic?")}</p>
                      </div>
                    }
                    options={UnlockFieldOptions}
                    className="required-field sheet-radio-group"
                  />
                </Grid.Column>
                {!isMssa && tieredTonerAvailableToDealer === true && (
                  <>
                    <Grid.Column
                      mobile={8}
                      tablet={8}
                      computer={3}
                      className="override-radio-group"
                    >
                      <Form.RadioGroup
                        inline
                        field="unlockAllowedTieredToner"
                        label={
                          <div className="override-radio-title">
                            <p>{t("Unlock_Allowed_Tiered_Toner?")}</p>
                          </div>
                        }
                        options={UnlockFieldOptions}
                        className="required-field sheet-radio-group"
                      />
                    </Grid.Column>
                    <Grid.Column
                      mobile={8}
                      tablet={8}
                      computer={3}
                      className="override-radio-group"
                    >
                      <Form.RadioGroup
                        inline
                        field="unlockOverrideMaximumTieredToner"
                        label={
                          <div className="override-radio-title">
                            <p>{t("Unlock_Override_Maximum_Tiered_Toner?")}</p>
                          </div>
                        }
                        options={UnlockFieldOptions}
                        className="required-field sheet-radio-group"
                      />
                    </Grid.Column>
                  </>
                )}
                {calcTableTypes.map((ctt) => {
                  const suffix = isMssa ? t(`enums:calcTableType.${ctt}`) : "";
                  return (
                    <div key={ctt} className="funder-type-rate-section">
                      <FunderRateTypeSection
                        funderType={FunderType.Funder}
                        header={`${t("Funder")} ${suffix}`}
                        className={classNames("funder", {
                          "software-assignment":
                            ctt === CalcTableType.SoftwareAssignment,
                        })}
                        UnlockFieldOptions={UnlockFieldOptions}
                        calcTableType={ctt}
                      />
                      <FunderRateTypeSection
                        funderType={FunderType.Dealer}
                        header={`${t("Supplier")} ${suffix}`}
                        className={classNames("supplier", {
                          "software-assignment":
                            ctt === CalcTableType.SoftwareAssignment,
                        })}
                        UnlockFieldOptions={UnlockFieldOptions}
                        calcTableType={ctt}
                      />
                      <FunderRateTypeSection
                        funderType={FunderType.CoFunded}
                        header={`${t("Co-Funded")} ${suffix}`}
                        className={classNames("co-funded", {
                          "software-assignment":
                            ctt === CalcTableType.SoftwareAssignment,
                        })}
                        UnlockFieldOptions={UnlockFieldOptions}
                        calcTableType={ctt}
                      />
                    </div>
                  );
                })}
              </Grid.Row>

              {calcTableTypes.length > 0 && (
                <Grid.Row columns={3}>
                  {calcTableTypes.map((ctt) => {
                    const suffix = isMssa
                      ? t(`enums:calcTableType.${ctt}`)
                      : "";
                    return (
                      <div key={ctt} className="override-section">
                        <OverrideSection
                          title={`${t("Funder_Overrides")} ${suffix}`}
                          className={classNames("funder", {
                            "software-assignment":
                              ctt === CalcTableType.SoftwareAssignment,
                          })}
                          funderType={FunderType.Funder}
                          calcTableType={ctt}
                        />
                        <OverrideSection
                          title={`${t("Supplier_Overrides")} ${suffix}`}
                          className={classNames("supplier", {
                            "software-assignment":
                              ctt === CalcTableType.SoftwareAssignment,
                          })}
                          funderType={FunderType.Dealer}
                          calcTableType={ctt}
                        />
                        <OverrideSection
                          title={`${t("Co-Funded_Overrides")} ${suffix}`}
                          className={classNames("co-funded", {
                            "software-assignment":
                              ctt === CalcTableType.SoftwareAssignment,
                          })}
                          funderType={FunderType.CoFunded}
                          calcTableType={ctt}
                        />
                      </div>
                    );
                  })}
                </Grid.Row>
              )}
            </Grid>
          </div>
        </Form.Object>
      </SheetStep>
    </div>
  );
};

export default connect(null)(withImpersonatedPermissions(IPSyndicateRatesStep));
